import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { BASE_TOKEN_CONFIG, SIBLING_SITE_TOKENS } from '@/client/confs/app';
import { useAuth } from '@/client/hooks/use-auth';
import { useAppSettingStore } from '@/client/store/app-store';
import { cn } from '@/client/utils';
import { switchSite } from '@/client/utils/site-switch';
import { Check, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import useMedia from 'react-use/lib/useMedia';
import { toast } from 'sonner';
import { useRoute } from 'wouter';

// Wallet currency lens: which base token the balance/portfolio widgets show.
// Dropdown rather than a segmented toggle — it's a low-frequency display
// preference, header space is scarce, and the list scales past two tokens.
// `inline` renders regardless of route (e.g. inside the wallet modal); the
// bare header instance still hides on market/invite pages. `compact` is a
// third instance — the market header's own mobile trigger (symbol + chevron,
// no coin icon, same dropdown incl. sibling sites) — and self-hides above md
// the same way SwitchLocale's `showOnMobile` does, so it can sit next to the
// existing `hidden md:block` desktop instance without that wrapper changing.
export const SwitchBaseToken = ({ inline = false, compact = false }: { inline?: boolean; compact?: boolean }) => {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const baseToken = useAppSettingStore(state => state.baseToken);
  const setBaseToken = useAppSettingStore(state => state.setBaseToken);
  // Detail pages pin the lens to the viewed market's collateral (see
  // useMarketLensSync); while pinned the switcher hides entirely — a market's
  // collateral is fixed, and switching would desync header balance / deposit
  // from what's being traded. Covers all instances (bare header + inline
  // wallet-modal + compact mobile header); the balance display around the
  // inline one stays.
  const lensLocked = useAppSettingStore(state => state.lensLockCount > 0);
  const [match] = useRoute(/^(?:\/market\/.*|\/user\/[^/]+\/(?:invite-earn))$/);
  const isDesktop = useMedia('(min-width: 768px)');
  if (lensLocked) return null;
  if (!inline && match) return null;
  if (compact && isDesktop) return null;
  if (BASE_TOKEN_CONFIG.length <= 1) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          title={t(baseToken.tooltipKey)}
          className={cn(
            'rounded-md gap-1.5 shadow-none',
            compact ? 'px-1.5' : 'px-2',
            // inline sits on the light wallet-card art: dark text + dark tints;
            // 44px touch target as the mobile entry point
            inline
              ? 'h-11 text-main-foreground border border-main-foreground/10 bg-main-foreground/5 hover:bg-main-foreground/10 hover:text-main-foreground'
              : 'h-9 border border-foreground/4 bg-foreground/4 hover:bg-foreground/8'
          )}
        >
          {/* compact drops the coin icon — symbol + chevron only, mobile header space is scarcer still */}
          {!compact && <baseToken.icon className="size-4.5" />}
          <span className="text-sm font-medium leading-5 text-current">{t(baseToken.translateKey)}</span>
          <ChevronDown className="size-3.5 op-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-40 max-w-64">
        {/* Caption: the lens filters the whole catalog, not just balances —
            say so before the user picks */}
        <div className="px-2 py-1.5 text-xs leading-4 text-muted-foreground">{t('currency-lens-caption')}</div>
        {BASE_TOKEN_CONFIG.map(item => {
          // Per-token descriptor (e.g. "Platform points" / "USDC-backed").
          // Empty default: tenants without a `<symbol>-token-desc` key render no subtitle
          const desc = t(`${item.symbol.toLowerCase()}-token-desc`, '');
          return (
            <DropdownMenuItem
              key={item.address}
              className={cn('gap-2 cursor-pointer', baseToken.address === item.address && 'bg-foreground/5')}
              onClick={() => setBaseToken(item)}
            >
              <item.icon className="size-4.5" />
              <span className="flex-1 min-w-0">
                <span className="flex items-center gap-2">
                  <span className="flex-1 text-sm font-medium">{t(item.translateKey)}</span>
                  <span className="text-xs text-muted-foreground">{item.name}</span>
                </span>
                {desc && <span className="block text-xs text-muted-foreground">{desc}</span>}
              </span>
              {baseToken.address === item.address && <Check className="size-3.5" />}
            </DropdownMenuItem>
          );
        })}
        {SIBLING_SITE_TOKENS.length > 0 && (
          <>
            <DropdownMenuSeparator />
            {SIBLING_SITE_TOKENS.map(item => (
              <DropdownMenuItem
                key={item.href}
                className="gap-2 cursor-pointer"
                // Finish logout before navigating into the other SPA.
                onSelect={() => {
                  void switchSite(item.href, logout).catch(() => toast.error(t('logout-error')));
                }}
              >
                <item.icon className="size-4.5" />
                <span className="flex-1 min-w-0">
                  <span className="flex items-center gap-2">
                    <span className="flex-1 text-sm font-medium">{item.label}</span>
                  </span>
                  <span className="block text-xs text-muted-foreground">{t('sibling-site-desc', 'Switch site')}</span>
                </span>
              </DropdownMenuItem>
            ))}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
